import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Trophy,
  Brain,
  Clock,
  Star,
  CheckCircle,
  XCircle,
  Loader2,
  Lock,
  Search,
  Filter,
  BarChart3,
  RefreshCw,
  Shuffle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function FlashcardsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.is_premium;
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [previewCards, setPreviewCards] = useState([]);
  const [dueCards, setDueCards] = useState([]);
  const [newCards, setNewCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState({});
  
  // Study session state
  const [isStudying, setIsStudying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionCards, setSessionCards] = useState([]);
  const [sessionStats, setSessionStats] = useState({ reviewed: 0, correct: 0, incorrect: 0 });
  
  // Filter state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryCards, setCategoryCards] = useState([]);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Always load preview cards first as fallback
      try {
        const previewRes = await axios.get(`${API}/api/flashcards/preview`);
        setPreviewCards(previewRes.data.cards || []);
      } catch (previewError) {
        console.error("Failed to load preview cards:", previewError);
      }
      
      // Load categories (public) - don't block on failure
      try {
        const catRes = await axios.get(`${API}/api/flashcards/categories`);
        setCategories(catRes.data.categories || {});
      } catch (catError) {
        console.error("Failed to load categories:", catError);
        setCategories({});
      }
      
      if (isPremium && token) {
        // Load premium data
        try {
          const [deckRes, statsRes] = await Promise.all([
            axios.get(`${API}/api/flashcards/deck`, { headers, timeout: 10000 }),
            axios.get(`${API}/api/flashcards/stats`, { headers, timeout: 10000 })
          ]);
          
          setDueCards(deckRes.data.due_cards || []);
          setNewCards(deckRes.data.new_cards || []);
          setStats(statsRes.data);
        } catch (premiumError) {
          console.error("Failed to load premium flashcard data:", premiumError);
          // Keep preview cards as fallback
        }
      }
    } catch (error) {
      console.error("Failed to load flashcards:", error);
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  }, [isPremium]);

  // Load data when auth is ready
  useEffect(() => {
    // Wait for auth to be checked before loading data
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);

  // Reload data if premium status changes (but not during study session)
  useEffect(() => {
    if (dataLoaded && !authLoading && !isStudying) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  // Shuffle array using Fisher-Yates algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Start study session with shuffled cards
  const startStudySession = (cards, shouldShuffle = true) => {
    if (cards.length === 0) {
      toast.info("No cards to study right now!");
      return;
    }
    const cardsToStudy = shouldShuffle ? shuffleArray(cards) : [...cards];
    setSessionCards(cardsToStudy);
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionStats({ reviewed: 0, correct: 0, incorrect: 0 });
    setIsStudying(true);
  };

  // Handle card review
  const handleReview = async (quality) => {
    const card = sessionCards[currentIndex];
    if (!card) return;
    
    // Update UI immediately for better UX
    const isLastCard = currentIndex >= sessionCards.length - 1;
    
    // Update session stats immediately
    setSessionStats(prev => ({
      reviewed: prev.reviewed + 1,
      correct: quality >= 3 ? prev.correct + 1 : prev.correct,
      incorrect: quality < 3 ? prev.incorrect + 1 : prev.incorrect
    }));
    
    // Move to next card immediately (don't wait for API)
    if (!isLastCard) {
      setCurrentIndex(prev => prev + 1);
      setIsFlipped(false);
    } else {
      // Session complete
      toast.success(
        <div>
          <p className="font-semibold">Session Complete!</p>
          <p className="text-sm">Reviewed {sessionStats.reviewed + 1} cards</p>
        </div>
      );
      setIsStudying(false);
    }
    
    // Record review in background (don't block UI)
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/flashcards/review/${card.id}`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { quality },
          timeout: 5000 // 5 second timeout
        }
      );
    } catch (error) {
      // Silently log error - don't show toast during study session
      console.error("Failed to record review for card:", card.id, error);
    }
    
    // Refresh data after session ends
    if (isLastCard) {
      setTimeout(() => loadData(), 500);
    }
  };

  // Search flashcards
  const handleSearch = async () => {
    if (!searchQuery.trim() || !isPremium) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/flashcards/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: searchQuery }
      });
      setSearchResults(res.data.results);
    } catch (error) {
      toast.error("Search failed");
    }
  };

  // Load cards by category
  const handleCategorySelect = async (category) => {
    if (selectedCategory === category) {
      // Deselect if clicking same category
      setSelectedCategory(null);
      setCategoryCards([]);
      return;
    }
    
    setSelectedCategory(category);
    setLoadingCategory(true);
    
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/api/flashcards/category/${encodeURIComponent(category)}`, { 
        headers,
        timeout: 10000 
      });
      setCategoryCards(res.data.cards || []);
    } catch (error) {
      console.error("Failed to load category cards:", error);
      toast.error("Failed to load cards for this category");
      setCategoryCards([]);
    } finally {
      setLoadingCategory(false);
    }
  };

  // Start study session for selected category
  const startCategorySession = () => {
    if (categoryCards.length === 0) {
      toast.info("No cards available in this category");
      return;
    }
    startStudySession(categoryCards);
  };

  // Current card in study session
  const currentCard = sessionCards[currentIndex];

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading flashcards...</p>
        </div>
      </div>
    );
  }

  // Study session view
  if (isStudying && currentCard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setIsStudying(false)}
            className="text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Exit
          </Button>
          <div className="text-white text-sm">
            {currentIndex + 1} / {sessionCards.length}
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-emerald-400">{sessionStats.correct} ✓</span>
            <span className="text-red-400">{sessionStats.incorrect} ✗</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-4">
          <Progress 
            value={(currentIndex / sessionCards.length) * 100} 
            className="h-1 bg-slate-700"
          />
        </div>

        {/* Flashcard */}
        <div className="flex-1 flex items-center justify-center p-4">
          <motion.div
            className="w-full max-w-2xl aspect-[3/2] cursor-pointer perspective-1000"
            onClick={() => setIsFlipped(!isFlipped)}
            data-testid="flashcard"
          >
            <motion.div
              className="w-full h-full relative"
              animate={{ rotateY: isFlipped ? 180 : 0 }}
              transition={{ duration: 0.6, type: "spring" }}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Front */}
              <div 
                className="absolute inset-0 backface-hidden bg-white rounded-2xl shadow-2xl p-8 flex flex-col"
                style={{ backfaceVisibility: "hidden" }}
              >
                <div className="text-xs text-teal-600 font-medium mb-4 uppercase tracking-wide">
                  {currentCard.category}
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center">
                    {currentCard.front}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm text-center mt-4">
                  Tap to reveal answer
                </p>
              </div>

              {/* Back */}
              <div 
                className="absolute inset-0 backface-hidden bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-2xl p-8 flex flex-col"
                style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
              >
                <div className="flex-1 flex items-center justify-center overflow-auto">
                  <p className="text-white text-lg md:text-xl leading-relaxed text-center">
                    {currentCard.back}
                  </p>
                </div>
                <p className="text-white/70 text-xs text-center mt-4 italic">
                  {currentCard.citation}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Rating buttons */}
        <AnimatePresence>
          {isFlipped && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="p-6 bg-slate-800/50 backdrop-blur"
            >
              <p className="text-white text-center mb-4 text-sm">How well did you know this?</p>
              <div className="flex justify-center gap-3 flex-wrap">
                <Button
                  onClick={() => handleReview(1)}
                  className="bg-red-500 hover:bg-red-600 text-white px-6"
                  data-testid="review-again"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Again
                </Button>
                <Button
                  onClick={() => handleReview(3)}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-6"
                  data-testid="review-hard"
                >
                  Hard
                </Button>
                <Button
                  onClick={() => handleReview(4)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
                  data-testid="review-good"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Good
                </Button>
                <Button
                  onClick={() => handleReview(5)}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6"
                  data-testid="review-easy"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Easy
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Main dashboard view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/quizzes")}
            className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Quizzes
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <BookOpen className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">ABA Flashcards</h1>
              <p className="text-white/80">
                400 terms & concepts with spaced repetition
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Premium Stats */}
        {isPremium && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.cards_studied}</p>
                  <p className="text-xs text-slate-500">Cards Studied</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.due_today}</p>
                  <p className="text-xs text-slate-500">Due Today</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.mastered}</p>
                  <p className="text-xs text-slate-500">Mastered</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.mastery_percentage}%</p>
                  <p className="text-xs text-slate-500">Mastery</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Study Options for Premium Users */}
        {isPremium ? (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Due Cards */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Review Due Cards</h3>
                    <p className="text-sm text-slate-500">{dueCards.length} cards ready for review</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => startStudySession(dueCards)}
                disabled={dueCards.length === 0}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300"
                data-testid="start-due-review"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {dueCards.length === 0 ? "No Cards Due" : "Start Review"}
              </Button>
            </motion.div>

            {/* New Cards */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Learn New Cards</h3>
                    <p className="text-sm text-slate-500">
                      {newCards.length > 0 ? `${newCards.length} new cards available` : "Loading cards..."}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => startStudySession(newCards.length > 0 ? newCards : previewCards)}
                disabled={newCards.length === 0 && previewCards.length === 0}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="start-new-cards"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Start Learning
              </Button>
            </motion.div>
          </div>
        ) : (
          /* Free Preview for Non-Premium Users */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mb-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Free Preview</h2>
                <p className="text-slate-500">Try 5 sample flashcards</p>
              </div>
            </div>

            <Button
              onClick={() => startStudySession(previewCards)}
              disabled={previewCards.length === 0}
              className="w-full bg-teal-600 hover:bg-teal-700 mb-4"
              data-testid="start-free-preview"
            >
              Start Free Preview
            </Button>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Unlock All 400 Flashcards</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Get access to all ABA terms with spaced repetition, progress tracking, and mastery stats.
                  </p>
                  <Button
                    onClick={() => navigate("/subscription")}
                    className="mt-3 bg-amber-500 hover:bg-amber-600"
                    size="sm"
                  >
                    Upgrade to Premium
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Categories</h3>
            {selectedCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedCategory(null); setCategoryCards([]); }}
                className="text-slate-500"
              >
                Clear Selection
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(categories).map(([category, count]) => (
              <button
                key={category}
                onClick={() => handleCategorySelect(category)}
                className={`text-left rounded-lg p-3 border transition-all duration-200 ${
                  selectedCategory === category
                    ? "bg-teal-50 border-teal-400 ring-2 ring-teal-200"
                    : "bg-slate-50 border-slate-200 hover:border-teal-300 hover:bg-teal-50/50"
                }`}
                data-testid={`category-${category.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <p className={`font-medium text-sm ${
                  selectedCategory === category ? "text-teal-700" : "text-slate-800"
                }`}>
                  {category}
                </p>
                <p className="text-xs text-slate-500">{count} cards</p>
              </button>
            ))}
          </div>

          {/* Selected Category Actions */}
          <AnimatePresence>
            {selectedCategory && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-slate-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-slate-800">{selectedCategory}</h4>
                    <p className="text-sm text-slate-500">
                      {loadingCategory ? "Loading..." : `${categoryCards.length} cards available`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-teal-600" />
                    <span className="text-sm text-teal-600 font-medium">Filtered</span>
                  </div>
                </div>
                
                <Button
                  onClick={startCategorySession}
                  disabled={loadingCategory || categoryCards.length === 0}
                  className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300"
                  data-testid="start-category-session"
                >
                  {loadingCategory ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading Cards...
                    </>
                  ) : (
                    <>
                      <Shuffle className="w-4 h-4 mr-2" />
                      Study All {categoryCards.length} Cards (Shuffled)
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-slate-500 text-center mt-2">
                  Cards will appear in random order
                </p>

                {/* Preview first few cards */}
                {categoryCards.length > 0 && !loadingCategory && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-slate-500 font-medium">Preview:</p>
                    <div className="grid gap-2">
                      {categoryCards.slice(0, 3).map((card, idx) => (
                        <div 
                          key={card.id} 
                          className="bg-slate-50 rounded-lg p-3 text-sm"
                        >
                          <span className="font-medium text-slate-700">{card.front}</span>
                        </div>
                      ))}
                      {categoryCards.length > 3 && (
                        <p className="text-xs text-slate-400 text-center">
                          + {categoryCards.length - 3} more cards
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Citation info */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>All content includes APA citations from Cooper, Heron & Heward (2020), Skinner (1953, 1957), Iwata et al., Bailey & Burch, and other foundational ABA literature.</p>
        </div>
      </div>
    </div>
  );
}
